import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { REGISTER } from '../graphql';
import { useAuth } from '../context/AuthContext';
import { ArrowRight, Eye, EyeOff, BookOpen, Building2, Users, Wallet } from 'lucide-react';
import AuraLogo from '../components/AuraLogo';

const FEATURES = [
  { icon: BookOpen,  label: 'Study Hub — notes, materials and attendance' },
  { icon: Building2, label: 'Campus Life — announcements, food and cab sharing' },
  { icon: Users,     label: 'Community — forums and the campus feed' },
  { icon: Wallet,    label: 'Money & Documents — expense groups and vault' },
];

const FIELDS = [
  { label: 'Full Name',   key: 'name',     type: 'text',     required: true,  placeholder: 'Rahul Sharma'           },
  { label: 'Email',       key: 'email',    type: 'email',    required: true,  placeholder: 'rahul@college.edu'      },
  { label: 'Password',    key: 'password', type: 'password', required: true,  placeholder: '8+ characters'          },
  { label: 'College',     key: 'college',  type: 'text',     required: false, placeholder: 'IIT Delhi'              },
  { label: 'Branch',      key: 'branch',   type: 'text',     required: false, placeholder: 'Computer Science'       },
  { label: 'Year',        key: 'year',     type: 'number',   required: false, placeholder: '1–4'                    },
];

export default function RegisterPage() {
  const [form, setForm]         = useState({ name:'', email:'', password:'', college:'', branch:'', year:'' });
  const [showPass, setShowPass] = useState(false);
  const [error, setError]       = useState('');
  const { login }               = useAuth();
  const navigate                = useNavigate();
  const [registerMutation, { loading }] = useMutation(REGISTER);

  const set = k => e => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const vars = { ...form, year: form.year ? parseInt(form.year) : null };
      const { data, errors } = await registerMutation({ variables: vars });
      if (errors?.length) throw new Error(errors[0].message);
      if (!data?.register) throw new Error('Registration failed. Please try again.');
      login(data.register.token, data.register.user);
      navigate('/');
    } catch (err) {
      const msg = err.message || '';
      if (msg.includes('status code') || msg.includes('NetworkError') || msg.includes('Failed to fetch')) {
        setError('Cannot reach server. Please try again in a moment.');
      } else {
        setError(msg || 'Registration failed. Please try again.');
      }
    }
  };

  return (
    <div className="min-h-screen flex bg-white dark:bg-surface-900">

      {/* ── Left panel — flat deep navy, same as login ── */}
      <div
        className="hidden lg:flex lg:w-[42%] relative flex-col justify-between p-14 overflow-hidden"
        style={{ background: '#0d3c78' }}
      >
        {/* Logo */}
        <Link to="/" className="relative z-10 flex items-center gap-3" style={{ textDecoration: 'none' }}>
          <AuraLogo size={36} />
          <span className="text-white font-bold text-xl tracking-tight">Aura</span>
        </Link>

        {/* Content */}
        <div className="relative z-10">
          <h2 className="text-4xl font-bold text-white leading-[1.15] mb-4">
            Your campus, organised.
          </h2>
          <p className="text-white/70 text-base leading-relaxed mb-8">
            Attendance, study materials, housing, expenses and campus community — in one place.
          </p>
          <div className="space-y-2.5">
            {FEATURES.map(f => (
              <div key={f.label} className="flex items-center gap-3 px-4 py-3 rounded-lg bg-white/[0.07] border border-white/10">
                <span className="w-7 h-7 rounded-md bg-white/10 flex items-center justify-center flex-shrink-0">
                  <f.icon size={14} className="text-white" />
                </span>
                <span className="text-white/80 text-sm font-medium">{f.label}</span>
              </div>
            ))}
          </div>
        </div>

        <p className="relative z-10 text-white/40 text-xs">
          © 2024 Quantaiseed
        </p>
      </div>

      {/* ── Right panel — form ── */}
      <div className="flex-1 flex flex-col justify-center items-center px-8 py-10 overflow-y-auto bg-white dark:bg-surface-900">

        {/* Mobile logo */}
        <div className="lg:hidden mb-8 self-start">
          <Link to="/" className="flex items-center gap-2.5" style={{ textDecoration: 'none' }}>
            <AuraLogo size={36} />
            <span className="font-bold text-gray-900 dark:text-white text-xl">Aura</span>
          </Link>
        </div>

        <div className="w-full max-w-[360px]">
          <div className="mb-7">
            <p className="text-xs font-semibold text-brand-600 dark:text-brand-400 uppercase tracking-widest mb-2">Get started</p>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Create your account</h2>
            <p className="text-gray-400 dark:text-white/40 mt-1 text-sm">Registration is free.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3.5">
            {FIELDS.map(({ label, key, type, required, placeholder }) => (
              <div key={key}>
                <label className="block text-xs font-semibold text-gray-500 dark:text-white/50 uppercase tracking-wide mb-1.5">
                  {label}
                </label>
                {key === 'password' ? (
                  <div className="relative">
                    <input className="input pr-11" type={showPass ? 'text' : 'password'}
                      placeholder={placeholder} required={required} value={form[key]} onChange={set(key)} />
                    <button type="button" onClick={() => setShowPass(s => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 dark:text-white/25 hover:text-gray-500 dark:hover:text-white/50 transition-colors">
                      {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                ) : (
                  <input className="input" type={type} placeholder={placeholder}
                    required={required} value={form[key]} onChange={set(key)} />
                )}
              </div>
            ))}

            {error && (
              <div className="flex items-start gap-2.5 px-4 py-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/40">
                <div className="w-4 h-4 rounded-full bg-red-100 dark:bg-red-800/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-red-500 text-[10px] font-black">!</span>
                </div>
                <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
              </div>
            )}

            <button type="submit" disabled={loading}
              className="w-full h-11 rounded-lg font-semibold text-white text-sm flex items-center justify-center gap-2 bg-brand-500 hover:bg-brand-600 transition-colors active:scale-[0.98] disabled:opacity-50 mt-1">
              {loading
                ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                : <><span>Create account</span><ArrowRight size={15} /></>}
            </button>
          </form>

          <p className="text-center text-sm text-gray-400 dark:text-white/40 mt-5">
            Already have an account?{' '}
            <Link to="/login" className="font-semibold text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 transition-colors">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
